@if (loading) {
  <div class="loading-container">
    <div class="loading-spinner"></div>
    <p>{{ 'manageEvent.orders.loading' | translate }}</p>
  </div>
} @else {
  <!-- Payment Settings Card -->
  <div class="card mb-8">
    <div class="card-header align-items-center">
      <div class="card-title m-0">
        <h3 class="fw-bold m-0">
          <i class="fa fa-credit-card me-2 text-primary"></i>
          {{ 'manageEvent.orders.paymentSettings' | translate }}
        </h3>
      </div>
      @if (!canConfigurePayment) {
        <span class="badge badge-light-warning">{{ 'manageEvent.orders.stripeNotConnected' | translate }}</span>
      } @else if (isCustomPaymentEnabled && !isStripeConnected) {
        <span class="badge badge-light-info">{{ 'manageEvent.orders.customPaymentActive' | translate }}</span>
      }
    </div>
    <div class="card-body p-9">
      @if (!canConfigurePayment) {
        <div class="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-6">
          <i class="fa fa-exclamation-triangle text-warning fs-2 me-4"></i>
          <div class="d-flex flex-stack flex-grow-1">
            <div class="fw-semibold">
              <h4 class="text-gray-900 fw-bold">{{ 'manageEvent.orders.paymentSetupRequired' | translate }}</h4>
              <div class="fs-6 text-gray-700">
                {{ 'manageEvent.orders.paymentSetupHint' | translate }}
                <a [routerLink]="['/company', company?.company_id]" [queryParams]="{tab: 'billing'}" class="fw-bold text-primary">{{ 'manageEvent.orders.billingSettings' | translate }}</a>.
              </div>
            </div>
          </div>
        </div>
      }
      @if (isCustomPaymentEnabled && !isStripeConnected) {
        <div class="notice d-flex bg-light-info rounded border-info border border-dashed p-6 mb-6">
          <i class="fa fa-info-circle text-info fs-2 me-4"></i>
          <div class="d-flex flex-stack flex-grow-1">
            <div class="fw-semibold">
              <h4 class="text-gray-900 fw-bold">{{ 'manageEvent.orders.customPaymentActiveTitle' | translate }}</h4>
              <div class="fs-6 text-gray-700">
                {{ 'manageEvent.orders.customPaymentActiveDesc' | translate }}
              </div>
            </div>
          </div>
        </div>
      }

      <div class="row">
        <!-- Payment Enabled Toggle -->
        <div class="col-md-6 mb-6">
          <label class="form-label fw-semibold">{{ 'manageEvent.orders.requirePayment' | translate }}</label>
          <div class="form-check form-switch form-check-custom form-check-solid">
            <input
              class="form-check-input"
              type="checkbox"
              [checked]="event.payment_enabled"
              [disabled]="!canConfigurePayment"
              (change)="togglePaymentEnabled($event)"
            />
            <label class="form-check-label fw-semibold text-gray-600 ms-3">
              {{ event.payment_enabled ? ('manageEvent.orders.enabled' | translate) : ('manageEvent.orders.disabled' | translate) }}
            </label>
          </div>
          <div class="text-muted fs-7 mt-2">
            {{ 'manageEvent.orders.requirePaymentHint' | translate }}
          </div>
        </div>

        <!-- Payment Timing -->
        <div class="col-md-6 mb-6">
          <label class="form-label fw-semibold">{{ 'manageEvent.orders.paymentTiming' | translate }}</label>
          <div class="d-flex flex-column gap-3">
            <label class="form-check form-check-custom form-check-solid" [class.opacity-50]="!isStripeConnected">
              <input
                class="form-check-input"
                type="radio"
                name="paymentTiming"
                value="immediate"
                [checked]="event.payment_timing === 'immediate'"
                [disabled]="!event.payment_enabled || !isStripeConnected"
                (change)="setPaymentTiming('immediate')"
              />
              <span class="form-check-label d-flex flex-column ms-3">
                <span class="fw-bold text-gray-800">{{ 'manageEvent.orders.immediatePayment' | translate }}</span>
                <span class="text-muted fs-7">{{ 'manageEvent.orders.immediatePaymentHint' | translate }}</span>
                @if (!isStripeConnected) {
                  <span class="text-warning fs-7">{{ 'manageEvent.orders.requiresStripe' | translate }}</span>
                }
              </span>
            </label>
            <label class="form-check form-check-custom form-check-solid">
              <input
                class="form-check-input"
                type="radio"
                name="paymentTiming"
                value="pay_later"
                [checked]="event.payment_timing === 'pay_later'"
                [disabled]="!event.payment_enabled"
                (change)="setPaymentTiming('pay_later')"
              />
              <span class="form-check-label d-flex flex-column ms-3">
                @if (isCustomPaymentEnabled && !isStripeConnected) {
                  <span class="fw-bold text-gray-800">{{ company?.custom_payment_method_name || ('manageEvent.orders.manualPayment' | translate) }}</span>
                  <span class="text-muted fs-7">{{ 'manageEvent.orders.customPaymentHint' | translate }}</span>
                } @else {
                  <span class="fw-bold text-gray-800">{{ 'manageEvent.orders.payLater' | translate }}</span>
                  <span class="text-muted fs-7">{{ 'manageEvent.orders.payLaterHint' | translate }}</span>
                }
              </span>
            </label>
          </div>
        </div>
      </div>

      @if (event.payment_enabled && canConfigurePayment) {
        <div class="separator my-6"></div>
        <div class="row">
          <div class="col-md-4">
            <div class="d-flex align-items-center">
              <div class="symbol symbol-40px me-4">
                <span class="symbol-label bg-light-success">
                  <i class="fa fa-check-circle text-success"></i>
                </span>
              </div>
              <div>
                <span class="text-gray-500 fw-semibold d-block fs-7">{{ 'manageEvent.orders.paymentStatus' | translate }}</span>
                <span class="text-success fw-bold">{{ 'manageEvent.orders.active' | translate }}</span>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="d-flex align-items-center">
              <div class="symbol symbol-40px me-4">
                <span class="symbol-label bg-light-info">
                  <i class="fa fa-clock text-info"></i>
                </span>
              </div>
              <div>
                <span class="text-gray-500 fw-semibold d-block fs-7">{{ 'manageEvent.orders.paymentTiming' | translate }}</span>
                <span class="text-gray-800 fw-bold">{{ event.payment_timing === 'immediate' ? ('manageEvent.orders.immediate' | translate) : ('manageEvent.orders.payLaterLabel' | translate) }}</span>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="d-flex align-items-center">
              <div class="symbol symbol-40px me-4">
                <span class="symbol-label" [ngClass]="isStripeConnected ? 'bg-light-primary' : 'bg-light-info'">
                  <i [ngClass]="isStripeConnected ? 'fab fa-stripe text-primary' : 'fa fa-hand-holding-dollar text-info'"></i>
                </span>
              </div>
              <div>
                <span class="text-gray-500 fw-semibold d-block fs-7">{{ 'manageEvent.orders.paymentProvider' | translate }}</span>
                <span class="text-gray-800 fw-bold">{{ isStripeConnected ? 'Stripe' : (company?.custom_payment_method_name || ('manageEvent.orders.manualPayment' | translate)) }}</span>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Order Statistics Dashboard -->
  <div class="row g-5 g-xl-8 mb-8">
    <!-- Total Orders -->
    <div class="col-xl-3 col-md-6">
      <div class="card bg-light-primary hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-shopping-cart text-primary fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ stats.totalOrders }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.totalOrders' | translate }}</div>
        </div>
      </div>
    </div>

    <!-- Paid Orders -->
    <div class="col-xl-3 col-md-6">
      <div class="card bg-light-success hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-check-circle text-success fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ stats.paidOrders }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.paidOrders' | translate }}</div>
        </div>
      </div>
    </div>

    <!-- Pending Orders -->
    <div class="col-xl-3 col-md-6">
      <div class="card bg-light-warning hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-clock text-warning fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ stats.pendingOrders }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.pendingOrders' | translate }}</div>
        </div>
      </div>
    </div>

    <!-- Total Revenue (ex fees) -->
    <div class="col-xl-3 col-md-6">
      <div class="card bg-light-info hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-money-bill-wave text-info fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ formatCents(stats.netPayout) }} {{ stats.currency }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.totalRevenueExFees' | translate }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Additional Stats Row -->
  <div class="row g-5 g-xl-8 mb-8">
    <!-- Pending Revenue -->
    <div class="col-xl-4 col-md-6">
      <div class="card bg-light-warning hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-hourglass-half text-warning fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ formatCents(stats.pendingRevenue) }} {{ stats.currency }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.pendingRevenue' | translate }}</div>
        </div>
      </div>
    </div>

    <!-- Failed Orders -->
    <div class="col-xl-4 col-md-6">
      <div class="card bg-light-danger hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-times-circle text-danger fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ stats.failedOrders }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.failedOrders' | translate }}</div>
        </div>
      </div>
    </div>

    <!-- Refunded Orders -->
    <div class="col-xl-4 col-md-6">
      <div class="card bg-light-secondary hoverable card-xl-stretch mb-xl-8">
        <div class="card-body">
          <i class="fa fa-undo text-secondary fs-2x mb-5 d-block"></i>
          <div class="text-gray-900 fw-bold fs-2 mb-2">{{ stats.refundedOrders }}</div>
          <div class="fw-semibold text-gray-500">{{ 'manageEvent.orders.refundedOrders' | translate }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Orders List Card -->
  <div class="card card-flush mb-9">
    <div class="card-header mt-5">
      <div class="card-title flex-column">
        <h3 class="fw-bold mb-1">{{ 'manageEvent.orders.ordersTitle' | translate }}</h3>
        <div class="fs-6 text-gray-500">{{ filteredOrders.length }} {{ 'manageEvent.orders.ordersFound' | translate }}</div>
      </div>
      <div class="card-toolbar my-1 d-flex gap-3">
        <!-- Search -->
        <div class="position-relative">
          <i class="fa fa-search position-absolute top-50 translate-middle-y ms-4 text-gray-500"></i>
          <input
            type="text"
            class="form-control form-control-solid ps-12"
            [placeholder]="'manageEvent.orders.searchPlaceholder' | translate"
            style="min-width: 250px;"
            [value]="searchQuery"
            (input)="onSearchChange($any($event.target).value)"
          />
        </div>
        <!-- Status Filter -->
        <select
          class="form-select form-select-solid"
          style="min-width: 150px;"
          [value]="statusFilter"
          (change)="onStatusFilterChange($any($event.target).value)"
        >
          <option value="all">{{ 'manageEvent.orders.allStatuses' | translate }}</option>
          <option value="paid">{{ 'manageEvent.orders.statusPaid' | translate }}</option>
          <option value="pending">{{ 'manageEvent.orders.statusPending' | translate }}</option>
          <option value="failed">{{ 'manageEvent.orders.statusFailed' | translate }}</option>
          <option value="refunded">{{ 'manageEvent.orders.statusRefunded' | translate }}</option>
          <option value="cancelled">{{ 'manageEvent.orders.statusCancelled' | translate }}</option>
        </select>
      </div>
    </div>
    <div class="card-body pt-0">
      @if (orders.length === 0) {
        <div class="text-center py-10 text-muted">
          <i class="fa fa-receipt fs-3x mb-4 d-block opacity-50"></i>
          <p class="mb-0">{{ 'manageEvent.orders.noOrders' | translate }}</p>
          <p class="fs-7">{{ 'manageEvent.orders.noOrdersHint' | translate }}</p>
        </div>
      } @else if (filteredOrders.length === 0) {
        <div class="text-center py-10 text-muted">
          <i class="fa fa-filter fs-3x mb-4 d-block opacity-50"></i>
          <p class="mb-0">{{ 'manageEvent.orders.noMatchFilter' | translate }}</p>
          <p class="fs-7">{{ 'manageEvent.orders.noMatchFilterHint' | translate }}</p>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="table table-row-bordered table-row-dashed gy-4 align-middle fw-bold">
            <thead class="fs-7 text-gray-500 text-uppercase">
              <tr>
                <th class="min-w-80px text-center">{{ 'manageEvent.orders.tableOrderNum' | translate }}</th>
                <th class="min-w-200px">{{ 'manageEvent.orders.tableCustomer' | translate }}</th>
                <th class="min-w-100px text-center">{{ 'manageEvent.orders.tableItems' | translate }}</th>
                <th class="min-w-100px text-center">{{ 'manageEvent.orders.tableTotal' | translate }}</th>
                <th class="min-w-100px text-center">{{ 'manageEvent.orders.tableStatus' | translate }}</th>
                <th class="min-w-120px text-center">{{ 'manageEvent.orders.tableCreated' | translate }}</th>
                <th class="min-w-120px text-center">{{ 'manageEvent.orders.tablePaidAt' | translate }}</th>
                <th class="text-end min-w-70px">{{ 'manageEvent.orders.tableActions' | translate }}</th>
              </tr>
            </thead>
            <tbody class="fs-6">
              @for (order of filteredOrders; track order.order_id) {
                <tr class="cursor-pointer" (click)="viewOrderDetails(order)">
                  <td class="text-center">
                    <span class="fw-bold text-primary">#{{ order.order_id }}</span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="fw-semibold text-gray-800">{{ getUserDisplayName(order) }}</span>
                      @if (getUserEmail(order)) {
                        <span class="text-muted fs-7">{{ getUserEmail(order) }}</span>
                      }
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="badge badge-light-primary">{{ getOrderLinesCount(order) }} {{ getOrderLinesCount(order) !== 1 ? ('manageEvent.orders.items' | translate) : ('manageEvent.orders.item' | translate) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="fw-bold">{{ formatCents(getOrderNetPayout(order)) }} {{ order.currency }}</span>
                  </td>
                  <td class="text-center">
                    <div class="d-flex flex-column gap-1 align-items-center">
                      <span class="badge {{ getStatusBadgeClass(order.status) }}">{{ getStatusLabel(order.status) }}</span>
                      @if (hasPartialRefund(order)) {
                        <span class="badge badge-light-info fs-8">{{ 'manageEvent.orders.partiallyRefunded' | translate }}</span>
                      }
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="text-gray-700">{{ formatDateTime(order.created_at) }}</span>
                  </td>
                  <td class="text-center">
                    @if (order.paid_at) {
                      <span class="text-gray-700">{{ formatDateTime(order.paid_at) }}</span>
                    } @else {
                      <span class="text-muted">-</span>
                    }
                  </td>
                  <td class="text-end" (click)="$event.stopPropagation()">
                    <div class="dropdown">
                      <button
                        class="btn btn-sm btn-icon btn-light btn-active-light-primary"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i class="fa fa-ellipsis-v"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end">
                        <li>
                          <a class="dropdown-item" (click)="viewOrderDetails(order)">
                            <i class="far fa-eye me-2"></i>
                            {{ 'manageEvent.orders.viewDetails' | translate }}
                          </a>
                        </li>
                        @if (order.status === 'pending') {
                          <li><hr class="dropdown-divider"></li>
                          <li class="dropdown-header">{{ 'manageEvent.orders.markAsPaid' | translate }}</li>
                          @if (isCustomPaymentEnabled && company?.custom_payment_method_name) {
                            <li>
                              <a class="dropdown-item" (click)="markOrderAsPaid(order, 'bank_transfer')">
                                <i class="fa fa-hand-holding-dollar me-2"></i>
                                {{ company?.custom_payment_method_name }}
                              </a>
                            </li>
                          } @else {
                            <li>
                              <a class="dropdown-item" (click)="markOrderAsPaid(order, 'bank_transfer')">
                                <i class="fa fa-university me-2"></i>
                                {{ 'manageEvent.orders.paymentBankTransfer' | translate }}
                              </a>
                            </li>
                            <li>
                              <a class="dropdown-item" (click)="markOrderAsPaid(order, 'cash')">
                                <i class="fa fa-money-bill me-2"></i>
                                {{ 'manageEvent.orders.paymentCash' | translate }}
                              </a>
                            </li>
                            <li>
                              <a class="dropdown-item" (click)="markOrderAsPaid(order, 'other')">
                                <i class="fa fa-credit-card me-2"></i>
                                {{ 'manageEvent.orders.paymentOther' | translate }}
                              </a>
                            </li>
                          }
                        }
                        @if (canRefund(order)) {
                          <li><hr class="dropdown-divider"></li>
                          <li>
                            <a class="dropdown-item text-danger" (click)="openRefundModal(order)">
                              <i class="fa fa-rotate-left me-2"></i>
                              {{ 'manageEvent.orders.refund' | translate }}
                            </a>
                          </li>
                        }
                      </ul>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
}
